import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import queryString from 'query-string';
import debounce from 'lodash/debounce';

import ContentWrapper from '../Layout/ContentWrapper';
import JobsTable from './Components/Tables/jobsTable';
import Pagination from './Components/shared/pagination';
import MostSuitableCandidatesModal from './Components/Modals/mostSuitableApplicantsModal';
import HelpModal from './Components/Modals/helpModal';
import { SpinnerLoader } from './Components/shared/spinnerLoader';

import * as jobService from '../Application/Services/jobService';
import * as adminService from '../Application/Services/adminService';
import * as utils from '../Application/Services/utils';
import useAuthStore from '../../stores/authStore';

import { Card, CardHeader, CardBody, CardFooter, Input, Button, Col } from 'reactstrap';
import Select from 'react-select';

const Jobs = () => {
  const { t } = useTranslation('translations');
  const history = useHistory();
  const location = useLocation();
  const user = useAuthStore((state) => state.user);

  const [MineOrAllSelected] = '1';

  // parse initial title from URL ?jobName=
  const initialTitle = useMemo(() => queryString.parse(location.search).jobName || '', [location.search]);
  const initialPageSize = parseInt(localStorage.getItem('numItemsPerPageJobs'), 10) || 10;

  // State
  const [jobsParams, setJobsParams] = useState({
    Title: initialTitle,
    Field: '',
    PageNumber: 1,
    PageSize: initialPageSize,
    MineOrAllSelected,
    JobStatus: null,
    EmploymentType: null,
    Experience: null,
    SelectedTenant: null,
  });
  const [searchText, setSearchText] = useState(initialTitle);
  const [jobs, setJobs] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingCandidates, setLoadingCandidates] = useState(true);
  const [mostSuitableCandidates, setMostSuitableCandidates] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);

  // Page‐size options
  const pageSizeOptions = useMemo(
    () => [
      { value: 10, label: '10' },
      { value: 20, label: '20' },
      { value: 50, label: '50' },
      { value: 100, label: '100' },
    ],
    []
  );
  const selectedPageSizeOption = pageSizeOptions.find((o) => o.value === jobsParams.PageSize);

  // Debounced search
  const debouncedSearch = useMemo(
    () =>
      debounce((text) => {
        setJobsParams((p) => ({ ...p, Title: text, PageNumber: 1 }));
      }, 1000),
    []
  );
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  // Fetch tenants (once if Admin)
  useEffect(() => {
    if (user.role === 'Admin') {
      adminService.getTenantsForAdminUser().then(({ data }) => setTenants(data));
    }
  }, [user.role]);

  // Fetch jobs whenever params change
  const fetchJobs = useCallback(
    async (params) => {
      setLoading(true);
      const effectiveParams = { ...params, MineOrAllSelected };
      const response =
        user.role === 'Admin'
          ? await adminService.getJobsFilteredWithTenant(effectiveParams)
          : await jobService.getJobs(effectiveParams);
      setJobs(response.data);
      setPagination(JSON.parse(response.headers.pagination));
      setLoading(false);
    },
    [user.role]
  );

  useEffect(() => {
    fetchJobs(jobsParams);
  }, [fetchJobs, jobsParams]);

  // Sync global MineOrAllSelected into local params
  // useEffect(() => {
  //   setJobsParams((p) => ({ ...p, MineOrAllSelected, PageNumber: 1 }));
  // }, [MineOrAllSelected]);

  // Handlers
  const handleSearchChange = (e) => {
    const txt = e.target.value;
    setSearchText(txt);
    debouncedSearch(txt);
  };
  const handlePageSizeChange = (option) => {
    setJobsParams((p) => ({
      ...p,
      PageSize: option.value,
      PageNumber: 1,
    }));
    localStorage.setItem('numItemsPerPageJobs', option.value);
  };
  const handlePageChange = (page) => setJobsParams((p) => ({ ...p, PageNumber: page }));
  const handleStatusChange = (item) => setJobsParams((p) => ({ ...p, JobStatus: item, PageNumber: 1 }));
  const handleEmploymentChange = (item) => setJobsParams((p) => ({ ...p, EmploymentType: item, PageNumber: 1 }));
  const handleExperienceChange = (item) => setJobsParams((p) => ({ ...p, Experience: item, PageNumber: 1 }));
  const handleTenantChange = (item) => {
    setJobsParams((p) => ({
      ...p,
      SelectedTenant: item,
      PageNumber: 1,
    }));
  };
  const clearFilters = () => {
    setSearchText('');
    setJobsParams((p) => ({
      ...p,
      Title: '',
      Field: '',
      JobStatus: null,
      EmploymentType: null,
      Experience: null,
      SelectedTenant: null,
      PageNumber: 1,
    }));
    history.push('/jobs');
  };
  const onCreateClick = () => history.push('/jobs/0');
  const onTableRequest = (id, item) =>
    history.push(`/jobs/${id}?name=testName&secondName=testSecondName&thirddName=testThirdName`);
  const onMostSuitable = (item) => {
    setMostSuitableCandidates(item);
    setLoadingCandidates(false);
    setShowModal(true);
  };
  const clearMostSuitable = () => {
    setMostSuitableCandidates([]);
    setLoadingCandidates(true);
  };
  //const toggleHelp = () => setShowHelpModal((v) => !v);

  return (
    <ContentWrapper>
      <MostSuitableCandidatesModal
        mostSuitableCandidates={mostSuitableCandidates}
        clearMostSuitableCandidatesData={clearMostSuitable}
        onToggle={() => setShowModal(false)}
        showModal={showModal}
        loadingMostSuitableCandidates={loadingCandidates}
      />
      <div className="content-heading">
        <Col md={10}>
          <Trans i18nKey="jobs.JOBS" />
          {/* <HelpModal purpose="jobsHelp" showModal={showHelpModal} onToggle={toggleHelp} /> */}
          <small>
            <Trans i18nKey="dashboard.WELCOME_BETA" />
          </small>
        </Col>
        <Col>
          <Button id="btnCreateNewJob" color="primary" size="lg" className="float-right" onClick={onCreateClick}>
            <b style={{ fontSize: '1.25rem' }}>{t('jobs.CREATE_NEW')}</b>
          </Button>
        </Col>
      </div>

      {loading ? (
        <SpinnerLoader />
      ) : (
        <Card className="card-default">
          <CardHeader>
            <div className="form-row align-items-center">
              <div className="col-auto">
                <Input
                  aria-label="Search by title"
                  type="text"
                  value={searchText}
                  placeholder={t('jobs.SEARCH_BY_TITLE')}
                  onChange={handleSearchChange}
                />
              </div>
              <div className="col-auto" style={{ width: 200 }}>
                <Select
                  value={jobsParams.JobStatus}
                  placeholder={t('jobs.SELECT_STATUS')}
                  onChange={handleStatusChange}
                  options={utils.getJobStatus()}
                  isClearable
                />
              </div>
              <div className="col-auto" style={{ width: 250 }}>
                <Select
                  value={jobsParams.EmploymentType}
                  placeholder={t('employmentType.TYPE')}
                  onChange={handleEmploymentChange}
                  options={utils.getEmploymentType()}
                  isClearable
                />
              </div>
              <div className="col-auto" style={{ width: 250 }}>
                <Select
                  value={jobsParams.Experience}
                  placeholder={t('experienceType.TYPE')}
                  onChange={handleExperienceChange}
                  options={utils.getExperienceType()}
                  isClearable
                />
              </div>
              <div className="col-auto">
                <Button color="primary" onClick={clearFilters}>
                  <Trans i18nKey="common.CLEAR_FILTERS" />
                </Button>
              </div>
            </div>

            {user.role === 'Admin' && (
              <>
                <hr />
                <strong>ADMIN PART</strong>
                <div className="form-row align-items-center mt-2">
                  <div className="col-auto" style={{ width: 500 }}>
                    <Select
                      value={jobsParams.SelectedTenant}
                      onChange={handleTenantChange}
                      options={tenants.map((t) => ({
                        value: t.id,
                        label: t.companyName ? `${t.companyName} - ${t.name}` : t.name,
                      }))}
                      placeholder="Select company..."
                      isSearchable
                      isClearable
                    />
                  </div>
                </div>
              </>
            )}
          </CardHeader>

          <CardBody>
            <JobsTable
              jobs={jobs}
              counting={(jobsParams.PageNumber - 1) * jobsParams.PageSize}
              onHandleTableRequest={onTableRequest}
              onMostSuitableCandidatesReceived={onMostSuitable}
              refreshStatus={() => fetchJobs(jobsParams)}
              onToggle={() => setShowModal((v) => !v)}
            />
          </CardBody>

          <CardFooter>
            <Pagination
              totalItems={pagination.totalItems}
              pageSize={jobsParams.PageSize}
              activePage={jobsParams.PageNumber}
              onPaginationSelect={handlePageChange}
              onTableSizeSelect={handlePageSizeChange}
              pagination={pagination}
              sizeOptions={pageSizeOptions}
              selectedSizeOption={selectedPageSizeOption}
            />
          </CardFooter>
        </Card>
      )}
    </ContentWrapper>
  );
};

export default Jobs;

// import React, { useState, useEffect, useMemo } from 'react';
// import { useTranslation, Trans } from 'react-i18next';
// import { useHistory, useLocation } from 'react-router-dom';
// import queryString from 'query-string';
// import debounce from 'lodash/debounce';

// import ContentWrapper from '../Layout/ContentWrapper';
// import JobsTable from './Components/Tables/jobsTable';
// import Pagination from './Components/shared/pagination';
// import MostSuitableCandidatesModal from './Components/Modals/mostSuitableApplicantsModal';
// import HelpModal from './Components/Modals/helpModal';
// import { SpinnerLoader } from './Components/shared/spinnerLoader';

// import * as jobService from '../Application/Services/jobService';
// import * as adminService from '../Application/Services/adminService';
// import * as utils from '../Application/Services/utils';
// import useAuthStore from '../../stores/authStore';

// import { Card, CardHeader, CardBody, CardFooter, Input, Button, Col } from 'reactstrap';
// import Select from 'react-select';

// const Jobs = () => {
//   const { t } = useTranslation('translations');
//   const history = useHistory();
//   const location = useLocation();
//   const user = useAuthStore((state) => state.user);

//   // If this is never changing, pull it out of state
//   const mineOrAllSelected = '1';

//   // parse ?jobName=
//   const initialTitle = useMemo(() => queryString.parse(location.search).jobName || '', [location.search]);
//   const initialPageSize = useMemo(() => parseInt(localStorage.getItem('numItemsPerPageJobs'), 10) || 10, []);

//   const [jobsParams, setJobsParams] = useState({
//     Title: initialTitle,
//     Field: '',
//     PageNumber: 1,
//     PageSize: initialPageSize,
//     JobStatus: null,
//     EmploymentType: null,
//     Experience: null,
//     SelectedTenant: null,
//   });
//   const [searchText, setSearchText] = useState(initialTitle);
//   const [jobs, setJobs] = useState([]);
//   const [tenants, setTenants] = useState([]);
//   const [pagination, setPagination] = useState({});
//   const [loading, setLoading] = useState(true);
//   const [loadingCandidates, setLoadingCandidates] = useState(true);
//   const [mostSuitableCandidates, setMostSuitableCandidates] = useState([]);
//   const [showModal, setShowModal] = useState(false);
//   const [showHelpModal, setShowHelpModal] = useState(false);

//   const pageSizeOptions = useMemo(
//     () => [
//       { value: 10, label: '10' },
//       { value: 20, label: '20' },
//       { value: 50, label: '50' },
//       { value: 100, label: '100' },
//     ],
//     []
//   );
//   const selectedPageSizeOption = pageSizeOptions.find((o) => o.value === jobsParams.PageSize);

//   // debounced text search
//   const debouncedSearch = useMemo(
//     () =>
//       debounce((text) => {
//         setJobsParams((p) => ({ ...p, Title: text, PageNumber: 1 }));
//       }, 1000),
//     []
//   );
//   useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

//   // load tenants if Admin
//   useEffect(() => {
//     if (user.role === 'Admin') {
//       adminService.getTenantsForAdminUser().then(({ data }) => setTenants(data));
//     }
//   }, [user.role]);

//   // --------- SINGLE EFFECT THAT LOADS JOBS ----------
//   useEffect(() => {
//     let isMounted = true;

//     const load = async () => {
//       setLoading(true);
//       const params = { ...jobsParams, MineOrAllSelected: mineOrAllSelected };

//       const response =
//         user.role === 'Admin' ? await adminService.getJobsFilteredWithTenant(params) : await jobService.getJobs(params);

//       if (!isMounted) return;
//       setJobs(response.data);
//       setPagination(JSON.parse(response.headers.pagination));
//       setLoading(false);
//     };

//     load();
//     return () => {
//       isMounted = false;
//     };
//   }, [jobsParams, mineOrAllSelected, user.role]);
//   // --------------------------------------------------

//   // Handlers
//   const handleSearchChange = (e) => {
//     const txt = e.target.value;
//     setSearchText(txt);
//     debouncedSearch(txt);
//   };

//   const handlePageSizeChange = (option) => {
//     setJobsParams((p) => ({ ...p, PageSize: option.value, PageNumber: 1 }));
//     localStorage.setItem('numItemsPerPageJobs', option.value);
//   };

//   const handlePageChange = (page) => setJobsParams((p) => ({ ...p, PageNumber: page }));

//   const handleStatusChange = (item) => setJobsParams((p) => ({ ...p, JobStatus: item, PageNumber: 1 }));

//   const handleEmploymentChange = (item) => setJobsParams((p) => ({ ...p, EmploymentType: item, PageNumber: 1 }));

//   const handleExperienceChange = (item) => setJobsParams((p) => ({ ...p, Experience: item, PageNumber: 1 }));

//   const handleTenantChange = (item) => setJobsParams((p) => ({ ...p, SelectedTenant: item, PageNumber: 1 }));

//   const clearFilters = () => {
//     setSearchText('');
//     setJobsParams((p) => ({
//       ...p,
//       Title: '',
//       Field: '',
//       JobStatus: null,
//       EmploymentType: null,
//       Experience: null,
//       SelectedTenant: null,
//       PageNumber: 1,
//     }));
//     history.push('/jobs');
//   };

//   const onCreateClick = () => history.push('/jobs/0');

//   const onTableRequest = (id, item) => history.push(`/jobs/${id}?name=testName`);

//   const onMostSuitable = (item) => {
//     setMostSuitableCandidates(item);
//     setLoadingCandidates(false);
//     setShowModal(true);
//   };
//   const clearMostSuitable = () => {
//     setMostSuitableCandidates([]);
//     setLoadingCandidates(true);
//   };
//   const toggleHelp = () => setShowHelpModal((v) => !v);

//   return (
//     <ContentWrapper>
//       <MostSuitableCandidatesModal
//         mostSuitableCandidates={mostSuitableCandidates}
//         clearMostSuitableCandidatesData={clearMostSuitable}
//         onToggle={() => setShowModal(false)}
//         showModal={showModal}
//         loadingMostSuitableCandidates={loadingCandidates}
//       />
//       <div className="content-heading">
//         <Col md={10}>
//           <Trans i18nKey="jobs.JOBS" />
//           <small>
//             <Trans i18nKey="dashboard.WELCOME_BETA" />
//           </small>
//         </Col>
//         <Col>
//           <Button id="btnCreateNewJob" color="primary" size="lg" className="float-right" onClick={onCreateClick}>
//             <b style={{ fontSize: '1.25rem' }}>{t('jobs.CREATE_NEW')}</b>
//           </Button>
//         </Col>
//       </div>

//       {loading ? (
//         <SpinnerLoader />
//       ) : (
//         <Card className="card-default">
//           <CardHeader>{/* … search inputs, filters, and (Admin) tenant picker … */}</CardHeader>
//           <CardBody>
//             <JobsTable
//               jobs={jobs}
//               counting={(jobsParams.PageNumber - 1) * jobsParams.PageSize}
//               onHandleTableRequest={onTableRequest}
//               onMostSuitableCandidatesReceived={onMostSuitable}
//               refreshStatus={() => {
//                 /* you can still call load manually if needed */
//               }}
//               onToggle={() => setShowModal((v) => !v)}
//             />
//           </CardBody>
//           <CardFooter>
//             <Pagination
//               totalItems={pagination.totalItems}
//               pageSize={jobsParams.PageSize}
//               activePage={jobsParams.PageNumber}
//               onPaginationSelect={handlePageChange}
//               onTableSizeSelect={handlePageSizeChange}
//               pagination={pagination}
//               sizeOptions={pageSizeOptions}
//               selectedSizeOption={selectedPageSizeOption}
//             />
//           </CardFooter>
//         </Card>
//       )}
//     </ContentWrapper>
//   );
// };

// export default Jobs;
